/**
 * Persistent store for SenderKey ratchet state, backed by IndexedDB.
 *
 * ## Storage layout
 *
 * One IndexedDB database named `kaether.sender-keys` with two object stores:
 *   - `states`  — key = storeKey(groupId, senderId, deviceId), value = serialised SenderKeyState
 *   - `index`   — key = storeKey, value = SenderKeyId (for allForGroup enumeration)
 *
 * The key is SHA-256(`groupId|senderId|deviceId`) in hex, so directory
 * enumeration alone cannot reverse-map filenames to (group, sender) pairs.
 * The index store is a convenience — losing it never loses a key; individual
 * state records remain authoritative.
 *
 * ## Rotation policy
 *
 * `rotate(groupId, senderId, deviceId)` generates a fresh local-owner state
 * — a fresh chainKey and a fresh Ed25519 signing keypair — so any party that
 * previously held the old state cannot decrypt future group messages. Callers
 * wire this into the membership change handler ("on member leave/kick, rotate
 * sender keys before the first post-change send").
 *
 * ## Web platform note
 *
 * iOS uses `.completeFileProtection` for at-rest encryption. The web platform
 * has no equivalent. We rely on SubtleCrypto with non-extractable keys where
 * possible, and document this as a known gap (see Signal_standard_web.md §J).
 *
 * ## iOS parity
 * Mirrors `kaether_for_ios/Stoat/E2EE/SenderKeyStore.swift` semantically.
 *
 * Pure logic + IndexedDB only. No SolidJS imports.
 */

import {
  generateSenderKeyState,
  CHAIN_KEY_LENGTH,
  type SenderKeyId,
  type SenderKeyState,
} from "./SenderKey";

// ─── serialisation helpers ────────────────────────────────────────────────────

function toBase64(bytes: Uint8Array): string {
  let binary = "";
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

function fromBase64(str: string): Uint8Array {
  let s = str;
  while (s.length % 4 !== 0) s += "=";
  const binary = atob(s);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

interface StoredState {
  chainKey: string; // base64
  chainIndex: number;
  signingPublicKey: string; // base64
  signingPrivateKey: string | null; // base64 or null
}

function serialiseState(state: SenderKeyState): StoredState {
  return {
    chainKey: toBase64(state.chainKeyBytes),
    chainIndex: state.chainIndex,
    signingPublicKey: toBase64(state.signingPublicKeyBytes),
    signingPrivateKey:
      state.signingPrivateKeyBytes !== null
        ? toBase64(state.signingPrivateKeyBytes)
        : null,
  };
}

function deserialiseState(s: StoredState): SenderKeyState {
  const chainKeyBytes = fromBase64(s.chainKey);
  if (chainKeyBytes.byteLength !== CHAIN_KEY_LENGTH) {
    throw new Error(
      `SenderKeyStore: invalid chainKey length on deserialise: ${chainKeyBytes.byteLength}`,
    );
  }
  return {
    chainKeyBytes,
    chainIndex: s.chainIndex,
    signingPublicKeyBytes: fromBase64(s.signingPublicKey),
    signingPrivateKeyBytes:
      s.signingPrivateKey !== null ? fromBase64(s.signingPrivateKey) : null,
  };
}

// ─── store key derivation ─────────────────────────────────────────────────────

const enc = new TextEncoder();

/**
 * SHA-256(`groupId|senderId|deviceId`) → hex string.
 * Prevents directory enumeration from revealing (group, sender) tuples.
 */
async function deriveStoreKey(id: SenderKeyId): Promise<string> {
  const raw = enc.encode(`${id.groupId}|${id.senderId}|${id.deviceId}`);
  const hashBuf = await crypto.subtle.digest("SHA-256", raw);
  return Array.from(new Uint8Array(hashBuf))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

// ─── IndexedDB helpers ────────────────────────────────────────────────────────

const DB_NAME = "kaether.sender-keys";
const DB_VERSION = 1;
const STATES_STORE = "states";
const INDEX_STORE = "index";

async function openDb(): Promise<IDBDatabase> {
  return new Promise<IDBDatabase>((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = (e) => {
      const db = (e.target as IDBOpenDBRequest).result;
      if (!db.objectStoreNames.contains(STATES_STORE)) {
        db.createObjectStore(STATES_STORE);
      }
      if (!db.objectStoreNames.contains(INDEX_STORE)) {
        db.createObjectStore(INDEX_STORE);
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function idbGet<T>(
  db: IDBDatabase,
  storeName: string,
  key: string,
): Promise<T | undefined> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, "readonly");
    const req = tx.objectStore(storeName).get(key);
    req.onsuccess = () => resolve(req.result as T | undefined);
    req.onerror = () => reject(req.error);
  });
}

function idbPut(
  db: IDBDatabase,
  storeName: string,
  key: string,
  value: unknown,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, "readwrite");
    const req = tx.objectStore(storeName).put(value, key);
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
}

function idbDelete(
  db: IDBDatabase,
  storeName: string,
  key: string,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, "readwrite");
    const req = tx.objectStore(storeName).delete(key);
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
}

function idbGetAllKeys(db: IDBDatabase, storeName: string): Promise<string[]> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, "readonly");
    const req = tx.objectStore(storeName).getAllKeys();
    req.onsuccess = () => resolve(req.result as string[]);
    req.onerror = () => reject(req.error);
  });
}

function idbGetAll<T>(db: IDBDatabase, storeName: string): Promise<T[]> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, "readonly");
    const req = tx.objectStore(storeName).getAll();
    req.onsuccess = () => resolve(req.result as T[]);
    req.onerror = () => reject(req.error);
  });
}

function idbClear(db: IDBDatabase, storeName: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, "readwrite");
    const req = tx.objectStore(storeName).clear();
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
}

// ─── SenderKeyStore ───────────────────────────────────────────────────────────

/**
 * IndexedDB-backed sender key store.
 *
 * `SenderKeyStore.shared` is the process-wide singleton for production use.
 * Tests should construct `new SenderKeyStore(dbName)` with a unique name per
 * test so they are isolated from each other and from production data.
 */
export class SenderKeyStore {
  private readonly dbName: string;
  private dbPromise: Promise<IDBDatabase> | null = null;

  constructor(dbName: string = DB_NAME) {
    this.dbName = dbName;
  }

  static readonly shared = new SenderKeyStore();

  private db(): Promise<IDBDatabase> {
    if (!this.dbPromise) {
      this.dbPromise = this.openDb();
    }
    return this.dbPromise;
  }

  private openDb(): Promise<IDBDatabase> {
    return new Promise<IDBDatabase>((resolve, reject) => {
      const req = indexedDB.open(this.dbName, DB_VERSION);
      req.onupgradeneeded = (e) => {
        const db = (e.target as IDBOpenDBRequest).result;
        if (!db.objectStoreNames.contains(STATES_STORE)) {
          db.createObjectStore(STATES_STORE);
        }
        if (!db.objectStoreNames.contains(INDEX_STORE)) {
          db.createObjectStore(INDEX_STORE);
        }
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    });
  }

  // ─── read / write ───────────────────────────────────────────────────────────

  async getState(id: SenderKeyId): Promise<SenderKeyState | null> {
    const db = await this.db();
    const key = await deriveStoreKey(id);
    const stored = await idbGet<StoredState>(db, STATES_STORE, key);
    if (!stored) return null;
    try {
      return deserialiseState(stored);
    } catch {
      return null;
    }
  }

  async setState(id: SenderKeyId, state: SenderKeyState): Promise<void> {
    const db = await this.db();
    const key = await deriveStoreKey(id);
    await idbPut(db, STATES_STORE, key, serialiseState(state));
    // Update index
    await idbPut(db, INDEX_STORE, key, {
      groupId: id.groupId,
      senderId: id.senderId,
      deviceId: id.deviceId,
    });
  }

  /**
   * Generates a fresh local-owner SenderKey and stores it under
   * (groupId, senderId, deviceId). Use this when membership decreases —
   * every previous holder of the old chain key is now unable to decrypt
   * future messages.
   */
  async rotate(
    groupId: string,
    senderId: string,
    deviceId: number,
  ): Promise<void> {
    const id: SenderKeyId = { groupId, senderId, deviceId };
    const fresh = await generateSenderKeyState();
    await this.setState(id, fresh);
  }

  async delete(id: SenderKeyId): Promise<void> {
    const db = await this.db();
    const key = await deriveStoreKey(id);
    await idbDelete(db, STATES_STORE, key);
    await idbDelete(db, INDEX_STORE, key);
  }

  /**
   * Returns every (groupId, senderId, deviceId) we hold state for in the
   * given group. Useful for "list members who have sent in this group"
   * debugging and for rotating every key on membership change.
   */
  async allForGroup(groupId: string): Promise<SenderKeyId[]> {
    const db = await this.db();
    const all = await idbGetAll<SenderKeyId>(db, INDEX_STORE);
    return all.filter((id) => id.groupId === groupId);
  }

  /**
   * Wipes all stored state. Used in tests and for account-wipe flows.
   */
  async resetAll(): Promise<void> {
    const db = await this.db();
    await idbClear(db, STATES_STORE);
    await idbClear(db, INDEX_STORE);
  }
}
